/** `/workspace/:id/room` — Design Rooms of a workspace: list + create (owner/facilitator). */
import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Loader2, Plus, Users } from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Textarea } from '#/components/ui/textarea'
import { Switch } from '#/components/ui/switch'
import { Field, StudioSection } from '#/components/studio/shared'
import {
  TEMPLATES,
  TEMPLATE_CONFIG,
  roomErrorText,
  stepLabel,
  templateLabel,
} from '#/components/room/room-templates'
import { ApiError, useWorkspacePlans } from '#/lib/api'
import { useCreateRoom, useRooms } from '#/lib/api-room'
import { fmtDate } from '#/lib/format'
import type { RoomBrief, RoomTemplate } from '#/lib/types'
import { cn } from '#/lib/utils'
import { getLocale } from '#/paraglide/runtime'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/workspace/$workspaceId/room/')({
  head: () => ({ meta: [{ title: m.rm_index_title() }] }),
  component: RoomsPage,
})

function RoomsPage() {
  const { workspaceId } = Route.useParams()
  const navigate = useNavigate()
  const { data: ws } = useWorkspacePlans(workspaceId)
  const { data, isLoading } = useRooms(workspaceId)
  const create = useCreateRoom(workspaceId)
  const [title, setTitle] = useState('')
  const [template, setTemplate] = useState<RoomTemplate>('dt5')
  const [language, setLanguage] = useState<'id' | 'en'>(
    getLocale() === 'en' ? 'en' : 'id',
  )
  const [anonymous, setAnonymous] = useState(false)
  const [blindVotes, setBlindVotes] = useState(true)
  const [scoreByAll, setScoreByAll] = useState(true)
  const [votes, setVotes] = useState(3)
  const [brief, setBrief] = useState<RoomBrief>({})
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    const b: RoomBrief = {}
    if (brief.context?.trim()) b.context = brief.context.trim().slice(0, 1500)
    if (brief.goal?.trim()) b.goal = brief.goal.trim().slice(0, 300)
    if (brief.audience?.trim()) b.audience = brief.audience.trim().slice(0, 200)
    if (brief.deadline) b.deadline = brief.deadline
    create.mutate(
      {
        title: title.trim(),
        template,
        language,
        brief: b,
        settings: { anonymous, votesPerPerson: votes, blindVotes, scoreByAll },
      },
      {
        onSuccess: (r) =>
          void navigate({
            to: '/workspace/$workspaceId/room/$roomId',
            params: { workspaceId, roomId: r.room.id },
          }),
        onError: (err) => {
          const code = err instanceof ApiError ? err.code : undefined
          toast.error(
            roomErrorText(code, err instanceof ApiError ? err.message : ''),
          )
          if (code === 'rooms_exhausted')
            void navigate({ to: '/pricing', search: { voucher: undefined } })
        },
      },
    )
  }
  const rooms = data?.rooms ?? []
  return (
    <>
      <AppHeader
        showStats={false}
        compact
        left={
          <nav className="hidden items-center gap-1 truncate text-sm text-muted-foreground lg:flex">
            <span>/</span>
            <Link
              to="/workspace/$workspaceId"
              params={{ workspaceId }}
              className="truncate hover:text-foreground"
            >
              {ws?.workspace.name ?? '…'}
            </Link>
            <span>/</span>
            <span className="truncate text-foreground">
              {m.rm_index_title()}
            </span>
          </nav>
        }
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">
              {m.rm_index_title()}
            </h1>
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
              {m.rm_index_desc()}
            </p>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">{m.st_loading()}</p>
          ) : rooms.length ? (
            <ul className="grid gap-2 sm:grid-cols-2">
              {rooms.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/workspace/$workspaceId/room/$roomId"
                    params={{ workspaceId, roomId: r.id }}
                    className="block rounded-2xl border bg-card p-4 shadow-sm transition-colors hover:border-primary/60"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-heading font-semibold">
                          {r.title}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {templateLabel(r.template)} ·{' '}
                          {m.rm_step_of({ n: r.step })} ·{' '}
                          {stepLabel(r.step, r.template)}
                        </div>
                      </div>
                      <Badge
                        variant={r.status === 'open' ? 'outline' : 'secondary'}
                        className="shrink-0 font-normal"
                      >
                        {r.status === 'open'
                          ? m.rm_status_open()
                          : r.status === 'handed_off'
                            ? m.rm_status_handed_off()
                            : m.rm_status_archived()}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users className="size-3.5" />{' '}
                        {m.rm_meta_participants({ n: r.participants })} ·{' '}
                        {m.rm_meta_cards({ n: r.cards })}
                      </span>
                      <span className="tabular-nums">
                        {fmtDate(r.updatedAt)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
              {m.rm_index_empty()}
            </p>
          )}

          <StudioSection title={m.rm_new_title()}>
            <form className="grid gap-4" onSubmit={submit}>
              <Field label={m.rm_new_name()} htmlFor="rm-title">
                <Input
                  id="rm-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={m.rm_new_name_ph()}
                  maxLength={120}
                  required
                />
              </Field>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-sm font-medium">
                  {m.rm_new_brief_title()}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {m.rm_new_brief_hint()}
                </p>
                <div className="mt-3 grid gap-3">
                  <Field label={m.rm_new_goal()} htmlFor="rm-goal">
                    <Input
                      id="rm-goal"
                      value={brief.goal ?? ''}
                      onChange={(e) =>
                        setBrief((b) => ({
                          ...b,
                          goal: e.target.value.slice(0, 300),
                        }))
                      }
                      placeholder={m.rm_new_goal_ph()}
                      maxLength={300}
                    />
                  </Field>
                  <Field label={m.rm_new_context()} htmlFor="rm-context">
                    <Textarea
                      id="rm-context"
                      value={brief.context ?? ''}
                      onChange={(e) =>
                        setBrief((b) => ({
                          ...b,
                          context: e.target.value.slice(0, 1500),
                        }))
                      }
                      placeholder={m.rm_new_context_ph()}
                      maxLength={1500}
                      rows={3}
                      className="text-sm"
                    />
                  </Field>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                    <Field label={m.rm_new_audience()} htmlFor="rm-audience">
                      <Input
                        id="rm-audience"
                        value={brief.audience ?? ''}
                        onChange={(e) =>
                          setBrief((b) => ({
                            ...b,
                            audience: e.target.value.slice(0, 200),
                          }))
                        }
                        placeholder={m.rm_new_audience_ph()}
                        maxLength={200}
                      />
                    </Field>
                    <Field
                      label={m.rm_new_deadline()}
                      htmlFor="rm-deadline"
                      className="w-44"
                    >
                      <Input
                        id="rm-deadline"
                        type="date"
                        value={brief.deadline ?? ''}
                        onChange={(e) =>
                          setBrief((b) => ({
                            ...b,
                            deadline: e.target.value || undefined,
                          }))
                        }
                      />
                    </Field>
                  </div>
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                  {m.rm_new_template()}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTemplate(t.id)}
                      aria-pressed={template === t.id}
                      className={cn(
                        'rounded-xl border p-3 text-left transition-colors',
                        template === t.id
                          ? 'border-foreground bg-muted/60'
                          : 'hover:bg-muted/40',
                      )}
                    >
                      <div className="text-sm font-medium">{t.label()}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {t.desc()}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {TEMPLATE_CONFIG[t.id].steps.map((s, i) => (
                          <span
                            key={i}
                            className="rounded-full border bg-background px-1.5 py-px text-[10px] text-muted-foreground"
                          >
                            <span className="mr-0.5 font-semibold text-foreground">
                              {i + 1}
                            </span>
                            {s.label()}
                          </span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <Field label={m.rm_new_language()}>
                  <div className="flex overflow-hidden rounded-md border text-sm">
                    {(['id', 'en'] as const).map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setLanguage(l)}
                        aria-pressed={language === l}
                        className={cn(
                          'px-3 py-1.5 uppercase',
                          language === l
                            ? 'bg-foreground text-background'
                            : 'hover:bg-muted',
                        )}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field
                  label={m.rm_new_votes()}
                  htmlFor="rm-votes"
                  className="w-28"
                >
                  <Input
                    id="rm-votes"
                    type="number"
                    min={1}
                    max={10}
                    value={votes}
                    onChange={(e) =>
                      setVotes(
                        Math.min(10, Math.max(1, Number(e.target.value) || 3)),
                      )
                    }
                  />
                </Field>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <label className="flex items-start gap-2 rounded-xl border p-3 text-sm">
                  <Switch
                    checked={blindVotes}
                    onCheckedChange={setBlindVotes}
                    aria-label={m.rm_new_blind()}
                  />
                  <span>
                    {m.rm_new_blind()}{' '}
                    <span className="block text-xs text-muted-foreground">
                      {m.rm_new_blind_hint()}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-xl border p-3 text-sm">
                  <Switch
                    checked={scoreByAll}
                    onCheckedChange={setScoreByAll}
                    aria-label={m.rm_new_scoreall()}
                  />
                  <span>
                    {m.rm_new_scoreall()}{' '}
                    <span className="block text-xs text-muted-foreground">
                      {m.rm_new_scoreall_hint()}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-xl border p-3 text-sm">
                  <Switch
                    checked={anonymous}
                    onCheckedChange={setAnonymous}
                    aria-label={m.rm_new_anonymous()}
                  />
                  <span>
                    {m.rm_new_anonymous()}{' '}
                    <span className="block text-xs text-muted-foreground">
                      {m.rm_new_anonymous_hint()}
                    </span>
                  </span>
                </label>
              </div>
              <Button
                type="submit"
                className="w-fit gap-1.5"
                disabled={create.isPending || !title.trim()}
              >
                {create.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Plus className="size-4" />
                )}{' '}
                {create.isPending ? m.rm_creating() : m.rm_create()}
              </Button>
            </form>
          </StudioSection>
        </div>
      </main>
    </>
  )
}
